<template>
  <mf-card-container style="height: 100%" title="Status dos clientes convertidos antes da campanha" :description="cardDescription">
    <mf-loading :boolean-loader="loading" :width="4" :height="70" loader-type="table">
      <div v-if="hasData">
        <v-data-table :items="statusCardChartData" :headers="headers" :options.sync="options" hide-default-footer disable-pagination>
          <template v-slot:item="{ item, index }">
            <tr>
              <td>
                <div class="legend-item">
                  <div class="item-square" :style="`background-color: ${item.color}`" />
                  {{ item.status }}
                </div>
              </td>
              <td>
                {{ item.customers.toLocaleString('pt-BR') }}
                <mf-quick-actions
                  v-show="chosenCustomerType === 'received_and_bought'"
                  v-if="quickActionByStatus(item.status, 'received_and_bought')"
                  :key="`${item.status}-${index}-received_and_bought`"
                  v-bind="quickActionByStatus(item.status, 'received_and_bought')"
                />
                <mf-quick-actions
                  v-show="chosenCustomerType === 'received_and_bought_with_offer_products'"
                  v-if="quickActionByStatus(item.status, 'received_and_bought_with_offer_products')"
                  :key="`${item.status}-${index}-received_and_bought_with_offer_products'`"
                  v-bind="quickActionByStatus(item.status, 'received_and_bought_with_offer_products')"
                />
                <mf-quick-actions
                  v-show="chosenCustomerType === 'received_and_bought_with_received_product'"
                  v-if="quickActionByStatus(item.status, 'received_and_bought_with_received_product')"
                  :key="`${item.status}-${index}-received_and_bought_with_received_product`"
                  v-bind="quickActionByStatus(item.status, 'received_and_bought_with_received_product')"
                />
              </td>
              <td>
                {{ item.revenue | localeString(2) | currency }}
              </td>
            </tr>
          </template>
        </v-data-table>
        <mf-info-card
          class="mt-2"
          description="A distribuição dos status dos clientes pré-campanha pode apresentar divergências em relação à configuração do disparo devido ao processamento assíncrono de cupons."
        />
      </div>
      <empty-chart v-else padding-top="15px" />
    </mf-loading>
  </mf-card-container>
</template>

<script>
import EmptyChart from '@/modules/mobile/desempenho/views/components/EmptyChart'
import { criteriaGroup, dynamicCampaignPreConversionStatusCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { DateTime } from 'luxon'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'StatusCard',
  components: {
    EmptyChart,
  },
  props: {
    dataChart: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: () => false,
    },
    chosenCustomerType: {
      type: String,
      required: true,
    },
    campaign: {
      type: Object,
      default: () => {},
    },
    dateCriteriaFilter: {
      type: Object,
      default: () => {},
    },
    units: {
      type: Array,
      default: () => [],
    },
    unitsConfiguration: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    options: {},
  }),
  computed: {
    ...mapGetters({
      primeiroRegistro: 'empresaPrimeiroRegistro',
    }),
    cardDescription() {
      let clientType = ''
      if (this.chosenCustomerType === 'received_and_bought') clientType = 'qualquer produto'
      if (this.chosenCustomerType === 'received_and_bought_with_offer_products') clientType = 'produtos da oferta'
      if (this.chosenCustomerType === 'received_and_bought_with_received_product') clientType = 'produto que recebeu via variável na mensagem'
      return `Mostra o status que os clientes, que compraram ${clientType} nesta campanha, possuiam antes do disparo.`
    },
    hasData() {
      if (!this.statusCardChartData.length) return false
      return this.statusCardChartData.reduce((total, valor) => total + valor.customers, 0)
    },
    headers() {
      return [
        {
          text: 'Status antes',
          value: 'status',
          width: '20%',
        },
        {
          text: 'Clientes',
          value: 'customers',
          width: '40%',
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          width: '40%',
        },
      ]
    },
    statusCardChartData() {
      if (!this.dataChart.before_bought) return []
      return [
        {
          status: 'Ativos',
          color: '#0FC94D',
          customers: this.dataChart.before_bought[this.chosenCustomerType].actives.clients,
          revenue: this.dataChart.before_bought[this.chosenCustomerType].actives.revenue,
        },
        {
          status: 'Inativos recentes',
          color: '#F3AF00',
          customers: this.dataChart.before_bought[this.chosenCustomerType].recent_inactives.clients,
          revenue: this.dataChart.before_bought[this.chosenCustomerType].recent_inactives.revenue,
        },
        {
          status: 'Risco de perder',
          color: '#F36600',
          customers: this.dataChart.before_bought[this.chosenCustomerType].at_risk.clients,
          revenue: this.dataChart.before_bought[this.chosenCustomerType].at_risk.revenue,
        },
        {
          status: 'Perdidos',
          color: '#B3280D',
          customers: this.dataChart.before_bought[this.chosenCustomerType].losses.clients,
          revenue: this.dataChart.before_bought[this.chosenCustomerType].losses.revenue,
        },
        {
          status: 'Nunca compraram',
          color: '#969EA7',
          customers: this.dataChart.before_bought[this.chosenCustomerType].never_bought.clients,
          revenue: this.dataChart.before_bought[this.chosenCustomerType].never_bought.revenue,
        },
      ]
    },
    aMonthBeforeCampaignStarts() {
      const date = this.campaign.initial_date
      return DateTime.fromFormat(date.toString(), 'yyyy-MM-dd').plus({ months: -1 }).toFormat('yyyyMMdd')
    },
    startDateForNeverBought() {
      // Start timestamp 1900-01-01
      return '19000101'
    },
    twoMonthsBeforeCampaignStarts() {
      const date = this.campaign.initial_date
      return DateTime.fromFormat(date.toString(), 'yyyy-MM-dd').plus({ days: -60 }).toFormat('yyyyMMdd')
    },
    twoMonthsAndOneDayBeforeCampaignStarts() {
      return DateTime.fromFormat(this.twoMonthsBeforeCampaignStarts.toString(), 'yyyyMMdd').plus({ days: -1 }).toFormat('yyyyMMdd')
    },
    aDayBeforeWithAMonthBefore() {
      return DateTime.fromFormat(this.aMonthBeforeCampaignStarts.toString(), 'yyyyMMdd').plus({ days: -1 }).toFormat('yyyyMMdd')
    },
    clientStart() {
      return this.primeiroRegistro ? DateTime.fromFormat(this.primeiroRegistro, 'yyyy-MM-dd HH:mm:ss').toFormat('yyyyMMdd') : ''
    },
  },
  watch: {
    options() {
      // Unset quick actions when changing the filter
      this.setIsIncludingSegments(false)
    },
  },
  methods: {
    ...mapActions(['setIsIncludingSegments']),

    quickActionByStatus(status, chosenCustomerType) {
      if (['Ativo', 'Ativos'].includes(status)) return this.quickActionsProps(chosenCustomerType)[0]
      if (['Inativo', 'Inativos recentes'].includes(status)) return this.quickActionsProps(chosenCustomerType)[1]
      if (['Perdido', 'Perdidos'].includes(status)) return this.quickActionsProps(chosenCustomerType)[2]
      if (['Nunca comprou', 'Nunca compraram'].includes(status)) return this.quickActionsProps(chosenCustomerType)[3]
      if (status === 'Risco de perder') return this.quickActionsProps(chosenCustomerType)[4]
    },

    quickActionsProps(chosenCustomerType) {
      let label = ''
      if (chosenCustomerType === 'received_and_bought') label = 'receberam e compraram produtos'
      if (chosenCustomerType === 'received_and_bought_with_offer_products') label = 'receberam e compraram produtos da oferta na campanha'
      if (chosenCustomerType === 'received_and_bought_with_received_product')
        label = 'receberam e compraram o produto que receberam via variável na mensagem da campanha'

      return [
        {
          key: `clients-${chosenCustomerType}-and-were-active`,
          quickActionGroupId: `clients-${chosenCustomerType}-and-were-active`,
          shortDescription: `Clientes que ${label} e estavam ativos`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title} e estavam ativos antes dessa compra`,
          segmentationGroup: () => criteriaGroup(this.getActiveCriteria(chosenCustomerType)),
        },
        {
          key: `clients-${chosenCustomerType}-and-were-inactive`,
          quickActionGroupId: `clients-${chosenCustomerType}-and-were-inactive`,
          shortDescription: `Clientes que ${label} e estavam inativos`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title} e estavam inativos antes dessa compra`,
          segmentationGroup: () => criteriaGroup(this.getInactiveCriteria(chosenCustomerType)),
        },
        {
          key: `clients-${chosenCustomerType}-and-were-lost`,
          quickActionGroupId: `clients-${chosenCustomerType}-and-were-lost`,
          shortDescription: `Clientes que ${label} e estavam perdidos`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title} e estavam perdidos antes dessa compra`,
          segmentationGroup: () => criteriaGroup(this.getLostCriteria(chosenCustomerType)),
        },
        {
          key: `clients-${chosenCustomerType}-and-had-never-bought`,
          quickActionGroupId: `clients-${chosenCustomerType}-and-had-never-bought`,
          shortDescription: `Clientes que ${label} e nunca haviam comprado`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title} e nunca haviam comprado antes dessa compra`,
          segmentationGroup: () => criteriaGroup(this.getNeverBoughtCriteria(chosenCustomerType)),
        },
        {
          key: `clients-${chosenCustomerType}-and-at-risk`,
          quickActionGroupId: `clients-${chosenCustomerType}-and-at-risk`,
          shortDescription: `Clientes que ${label} e estavam em risco`,
          longDescription: `Clientes que compraram na campanha ${this.campaign.title} e estavam em risco antes dessa compra`,
          segmentationGroup: () => criteriaGroup(this.getAtRiskCriteria(chosenCustomerType)),
        },
      ]
    },
    getActiveCriteria(chosenCustomerType) {
      const recencyCriteria = this.$getLoggedUser().recencyCriteria.actives
      return dynamicCampaignPreConversionStatusCriteria(
        [this.campaign._id],
        chosenCustomerType,
        recencyCriteria.bought[0],
        recencyCriteria.bought[1],
        this.unitsConfiguration
      )
    },
    getInactiveCriteria(chosenCustomerType) {
      const recencyCriteria = this.$getLoggedUser().recencyCriteria.recent_inactives
      return dynamicCampaignPreConversionStatusCriteria(
        [this.campaign._id],
        chosenCustomerType,
        recencyCriteria.bought[0],
        recencyCriteria.bought[1],
        this.unitsConfiguration
      )
    },
    getLostCriteria(chosenCustomerType) {
      const recencyCriteria = this.$getLoggedUser().recencyCriteria.losses
      return dynamicCampaignPreConversionStatusCriteria([this.campaign._id], chosenCustomerType, recencyCriteria.bought[0], 365 * 20, this.unitsConfiguration)
    },
    getNeverBoughtCriteria(chosenCustomerType) {
      console.log('chosenCustomerType', chosenCustomerType)
      return dynamicCampaignPreConversionStatusCriteria([this.campaign._id], chosenCustomerType, 365 * 20 + 1, 365 * 100, this.unitsConfiguration)
    },
    getAtRiskCriteria(chosenCustomerType) {
      const recencyCriteria = this.$getLoggedUser().recencyCriteria.at_risk
      return dynamicCampaignPreConversionStatusCriteria(
        [this.campaign._id],
        chosenCustomerType,
        recencyCriteria.bought[0],
        recencyCriteria.bought[1],
        this.unitsConfiguration
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;

  .item-square {
    width: 12px;
    height: 12px;
    border-radius: 4px;
  }
}
</style>
